import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export interface DropdownMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  title?: string;
}

export function DropdownMenu({ trigger, items, align = 'right', className }: {
  trigger: ReactNode;
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = Math.min(320, items.length * 40 + 8);
    const menuWidth = 208;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    const left = align === 'left' ? rect.left : Math.max(8, rect.right - menuWidth);
    const top = openUp ? Math.max(8, rect.top - menuHeight - 8) : rect.bottom + 8;
    setBox({ top, left });
  }, [open, items.length, align]);

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className={cn('relative h-full', className)}>
      <div className="h-full" ref={triggerRef} onClick={() => setOpen((value) => !value)}>{trigger}</div>
      {open && box ? createPortal(
        <div ref={menuRef} style={{ top: box.top, left: box.left }} className="fixed z-50 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {items.map((item) => (
            <button key={item.label} type="button" title={item.title} disabled={item.disabled} className={cn('flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:hover:bg-transparent', item.className)} onClick={() => { if (item.disabled) return; setOpen(false); item.onClick(); }}>
              {item.icon ? <span className="mr-3">{item.icon}</span> : null}
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
